import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AbstractComponent, Channel, ChannelService, ChannelTypeEnum, Company, CompanyService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { buildMetaOAuthUrl } from '../meta-oauth.util';

/** Type-picker value for a Cloud API channel on the Solution Partner's credit line. */
const CLOUD_API_CREDIT_LINE_OPTION = 'CLOUD_API_CREDIT_LINE';
/** Type-picker value for a Hybrid WhatsApp channel (SPEC-2026-10-whatsapp-hybrid-channel RF-01). */
const CLOUD_API_HYBRID_OPTION = 'CLOUD_API_HYBRID';

@Component({
    selector: 'channel-form-component',
    // templateUrl: './channel-form.component.html',
    templateUrl: './channel-form.v2.component.html',
    styleUrls: ['./channel-form.component.scss', './channel-form.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelFormComponent extends AbstractComponent {

  channel: Channel;
  channels: Array<Channel> = new Array<Channel>();
  hasFormGroup = false;

  breadcrumb = [
    { href: '/channel', label: 'channel.breadcrumb.channels' },
    { label: 'channel.breadcrumb.addChannel' },
  ]

  cloudAPI: ChannelTypeEnum = ChannelTypeEnum.CLOUD_API;
  zAPI: ChannelTypeEnum = ChannelTypeEnum.Z_API;
  instagramAPI: ChannelTypeEnum = ChannelTypeEnum.INSTAGRAM_API;
  messengerAPI: ChannelTypeEnum = ChannelTypeEnum.MESSENGER_API;
  rcsAPI: ChannelTypeEnum = ChannelTypeEnum.RCS_API;
  voipAPI: ChannelTypeEnum = ChannelTypeEnum.VOIP_API;
  webchatAPI: ChannelTypeEnum = ChannelTypeEnum.WEBCHAT_API;
  // UI-only option: still a CLOUD_API channel, persisted with `creditLine: true`
  readonly cloudAPICreditLine = CLOUD_API_CREDIT_LINE_OPTION;
  // UI-only option: still a CLOUD_API channel, persisted with `hybrid: true` (own quota, never credit line)
  readonly cloudAPIHybrid = CLOUD_API_HYBRID_OPTION;

  company: Company = undefined;

  instagramUrl = buildMetaOAuthUrl(ChannelTypeEnum.INSTAGRAM_API);
  messengerUrl = buildMetaOAuthUrl(ChannelTypeEnum.MESSENGER_API);

  constructor(
    injector: Injector,
    public companyService: CompanyService,
    public channelService: ChannelService,
  ) {
    super(injector);
    this.channel = { metadata: {} } as Channel;
    this.setupForm();
  }

  ngOnInit() {
    this.getCompanyById();
    this.getAllChannelsByCompany();

    this.isNew = true;
    const idChannel = super.getParam('idChannel');
    if (!!idChannel) {
      this.isNew = false;
      this.getChannelById(idChannel);
    }
  }

  private setupForm(): void {
    this.formGroup = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      type: new FormControl(null, [Validators.required]),
      // What the type picker is bound to: a ChannelTypeEnum value or CLOUD_API_CREDIT_LINE_OPTION.
      // Never persisted — onChannelOptionChange() resolves it into `type` + `creditLine` + `hybrid`.
      channelOption: new FormControl(null, [Validators.required]),
      creditLine: new FormControl(false),
      hybrid: new FormControl(false),
      integration: new FormControl(false),
      credentials: new FormGroup({
        // for type Z_API
        id: new FormControl(null, []),
        name: new FormControl(null, []),

        // for types CLOUD_API or Z_API
        token: new FormControl(null, []),

        // for type CLOUD_API
        phoneNumberID: new FormControl(null, []),
        whatsAppBusinessAccountID: new FormControl(null, []),

        // for type RCS_API — the account only. The provider's client secret is one token for the
        // whole Pontal organization, so the server stamps it from its own environment and the
        // operator is never asked for it.
        account: new FormControl(null, []),

        // for type VOIP_API (Issabel — SIP/WebRTC): { name(canal), username, secret, server, port }
        server: new FormControl(null, []),
        port: new FormControl(null, []),
        username: new FormControl(null, []),
        secret: new FormControl(null, []),
      }),
      // for type RCS_API — persisted into channel.metadata. Every RCS channel is conversational,
      // so the value is fixed here and the switch that used to set it is commented out in the template.
      rcs: new FormGroup({
        conversational: new FormControl(true),
      }),
      // for type WEBCHAT_API — widget customization, persisted into channel.metadata (RF-04/RF-25)
      webchat: new FormGroup({
        primaryColor: new FormControl(null, []),
        logoUrl: new FormControl(null, []),
        buttonPosition: new FormControl('right', []),
        privacyPolicyUrl: new FormControl(null, []),
      }),
    });
    this.formGroup.valueChanges.subscribe(value => {
      if (value) {
        Object.assign(this.channel, value);
        this.hasFormGroup = true;
      }
    });
  }

  getCompanyById() {
    this.companyService.getById(super.getIDCurrentCompany())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (company: Company) => {
          this.company = company;
        },
      });
  }

  getChannelById(idChannel: string) {
    this.channelService.getById(idChannel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => {
          this.channel = channel;
          this.formGroup.patchValue(this.channel);
          this.formGroup.get('channelOption').setValue(this.optionOf(channel), { emitEvent: false });
          // RCS: `conversational` is not rehydrated from the stored channel on purpose — it is
          // always true, so a channel saved before this change is corrected on the next save.
          if (channel.type === ChannelTypeEnum.WEBCHAT_API) {
            this.formGroup.get('webchat').patchValue(channel.metadata ?? {});
          }
        },
      });
  }

  getAllChannelsByCompany() {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels: Array<Channel>) => {
          this.channels = channels;
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  save() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    };

    // RCS: the conversational capability lives in channel.metadata, alongside the
    // rest of the provider configuration (never a top-level "rcs" field)
    if (this.channel.type === ChannelTypeEnum.RCS_API) {
      this.channel.metadata = { ...(this.channel.metadata ?? {}), ...this.formGroup.get('rcs').value };
    }
    delete (this.channel as any).rcs;

    // Webchat: customization lives in channel.metadata (never a top-level "webchat" field)
    if (this.channel.type === ChannelTypeEnum.WEBCHAT_API) {
      this.channel.metadata = { ...(this.channel.metadata ?? {}), ...this.formGroup.get('webchat').value };
    }
    delete (this.channel as any).webchat;
    delete (this.channel as any).channelOption;

    this.loading = true;

    if (this.isNew) {
      this.channelService.create(this.channel).subscribe({
        next: () => this.alertService.success(),
        error: (err) => this.alertService.error(err.error.message),
        complete: () => {
          this.loading = false;
          this.goBack();
        }
      });
    } else {
      this.channelService.update(this.channel._id, this.channel).subscribe({
        next: () => this.alertService.success(this.t('channel.channelForm.channelUpdatedSuccess')),
        error: (err) => this.alertService.error(err.error.message),
        complete: () => {
          this.loading = false;
          this.goBack();
        }
      });
    }
  }

  /** The type-picker option a stored channel maps back to. */
  private optionOf(channel: Channel): ChannelTypeEnum | string {
    if (channel.type !== ChannelTypeEnum.CLOUD_API) return channel.type;
    if (channel.creditLine) return CLOUD_API_CREDIT_LINE_OPTION;
    if (channel.hybrid) return CLOUD_API_HYBRID_OPTION;
    return channel.type;
  }

  /**
   * Whether the quota of a type-picker option is used up. Regular, credit-line and hybrid Cloud API
   * channels share the CLOUD_API type but have separate quotas, so each counts only its own kind.
   */
  checkIfExceded(option: ChannelTypeEnum | string): boolean {
    if (!this.company || !option) {
      return false;
    }
    const current = this.channels.filter((c) => this.optionOf(c) === option).length;

    const ec = this.company.enabledChannel;
    const maxByOption: Partial<Record<string, number>> = {
      [ChannelTypeEnum.CLOUD_API]: ec?.maxCloudAPI || 0,
      [CLOUD_API_CREDIT_LINE_OPTION]: ec?.maxCloudAPICreditLine || 0,
      [CLOUD_API_HYBRID_OPTION]: ec?.maxCloudAPIHybrid || 0,
      [ChannelTypeEnum.Z_API]: ec?.maxZAPI || 0,
      [ChannelTypeEnum.INSTAGRAM_API]: ec?.maxInstagram || 0,
      [ChannelTypeEnum.MESSENGER_API]: ec?.maxMessenger || 0,
      [ChannelTypeEnum.RCS_API]: ec?.maxRCS || 0,
      [ChannelTypeEnum.VOIP_API]: ec?.maxVoip || 0,
    };
    if (this.isNew) {
      maxByOption[ChannelTypeEnum.WEBCHAT_API] = ec?.maxWebchat || 0;
    }
    const max = maxByOption[option];
    return max != null && current >= max;
  }

  /**
   * On an existing channel the Cloud API marks are fixed (credit line and Hybrid are set at creation,
   * and the server drops any change): an option that would add, remove or swap a mark is locked.
   */
  isOptionLocked(option: ChannelTypeEnum | string): boolean {
    if (this.isNew || !this.channel?._id) return false;
    const current = this.optionOf(this.channel);
    if (option === current) return false;
    const marked: Array<ChannelTypeEnum | string> = [CLOUD_API_CREDIT_LINE_OPTION, CLOUD_API_HYBRID_OPTION];
    const cloudOptions: Array<ChannelTypeEnum | string> = [ChannelTypeEnum.CLOUD_API, ...marked];
    return cloudOptions.includes(option) && (marked.includes(option) || marked.includes(current));
  }

  /** Resolves the picked option into the persisted `type` + `creditLine` + `hybrid` marks. */
  onChannelOptionChange(): void {
    const option = this.formGroup.get('channelOption').value;
    const isCreditLine = option === CLOUD_API_CREDIT_LINE_OPTION;
    const isHybrid = option === CLOUD_API_HYBRID_OPTION;
    this.formGroup.patchValue({
      type: isCreditLine || isHybrid ? ChannelTypeEnum.CLOUD_API : option,
      creditLine: isCreditLine,
      hybrid: isHybrid,
    });
    this.valueChangeType();
  }

  valueChangeType() {
    const type = this.formGroup.get('type').value;
    if (type === ChannelTypeEnum.CLOUD_API) {
       this.formGroup.get('name').setValidators([Validators.required]);
    } else {
       this.formGroup.get('name').clearValidators();
    }
    if (this.formGroup.get('type')?.value === ChannelTypeEnum.INSTAGRAM_API || this.formGroup.get('type')?.value === ChannelTypeEnum.MESSENGER_API) {
      this.formGroup.get('name').setValidators([Validators.required]);
    } else {
      this.formGroup.get('name').clearValidators();
    }
    if (this.formGroup.get('type')?.value === ChannelTypeEnum.RCS_API) {
      this.formGroup.get('name').setValidators([Validators.required]);
      this.formGroup.get('credentials').get('account').setValidators([Validators.required]);
    } else {
      this.formGroup.get('name').clearValidators();
      this.formGroup.get('credentials').get('account').clearValidators();
    }
    if (this.formGroup.get('type')?.value === ChannelTypeEnum.VOIP_API) {
      this.formGroup.get('name').setValidators([Validators.required]);
      this.formGroup.get('credentials').get('server').setValidators([Validators.required]);
      this.formGroup.get('credentials').get('port').setValidators([Validators.required]);
      this.formGroup.get('credentials').get('username').setValidators([Validators.required]);
      this.formGroup.get('credentials').get('secret').setValidators([Validators.required]);
    } else {
      this.formGroup.get('credentials').get('server').clearValidators();
      this.formGroup.get('credentials').get('port').clearValidators();
      this.formGroup.get('credentials').get('username').clearValidators();
      this.formGroup.get('credentials').get('secret').clearValidators();
    }
    if (this.formGroup.get('type')?.value === ChannelTypeEnum.WEBCHAT_API) {
      // Webchat needs only a name — no external credentials (RF-01/RF-02)
      this.formGroup.get('name').setValidators([Validators.required]);
    }
    this.formGroup.get('credentials').get('token').updateValueAndValidity();
    this.formGroup.get('credentials').get('phoneNumberID').updateValueAndValidity();
    this.formGroup.get('credentials').get('whatsAppBusinessAccountID').updateValueAndValidity();
    this.formGroup.get('credentials').get('account').updateValueAndValidity();
    this.formGroup.get('credentials').get('server').updateValueAndValidity();
    this.formGroup.get('credentials').get('port').updateValueAndValidity();
    this.formGroup.get('credentials').get('username').updateValueAndValidity();
    this.formGroup.get('credentials').get('secret').updateValueAndValidity();
    this.formGroup.get('name').updateValueAndValidity();
  }

  getName() {
    return this.formGroup.get('name').value;
  }

  goClick() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    };
  }

}
